import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Settings as SettingsIcon, ArrowLeft, Pencil, Library } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatToday, formatClock } from "@/lib/status";
import { companyTypeLabel } from "@/lib/companyTypes";

/**
 * AppHeader — used on both routes.
 *
 * Props:
 *   variant: "overview" (clients page) | "client" (single client dashboard)
 *   client: { id, name, type } — when variant="client"
 *   onBack, onEditClient — client variant only
 *   primaryLabel: label for primary action button
 *   onPrimary: handler
 *   onOpenSettings: opens Settings modal
 */
export default function AppHeader({
  variant,
  client,
  onBack,
  onEditClient,
  primaryLabel,
  primaryTestId,
  onPrimary,
  onOpenSettings,
  onOpenLibrary,
}) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 30000,
  });
  const dbConnected = health?.database === "connected";

  return (
    <header
      className="sticky top-0 z-40 bg-[#F8F9FA]/95 backdrop-blur-sm border-b border-[#E2E8F0]"
      data-testid="app-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="md:hidden flex items-center gap-2.5 min-w-0">
            <img
              src="/brand/ndm-logo.jpg"
              alt="NDM Advisors"
              className="h-10 w-10 rounded-lg shrink-0 select-none object-cover"
              draggable={false}
            />
            <div className="min-w-0">
              <div
                className="text-sm font-semibold text-[#0F172A] leading-none truncate"
                data-testid={TID.headerCompany}
              >
                NDM Advisors
              </div>
              <div className="text-[11px] uppercase tracking-wider text-[#64748B] mt-1">
                Compliance Dashboard
              </div>
            </div>
          </div>

          {variant === "client" && client && (
            <div className="hidden md:flex items-center gap-2 pl-4 ml-2 border-l border-[#E2E8F0] min-w-0">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  data-testid={TID.clientBackBtn}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]"
                  title="All Clients"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div className="min-w-0">
                <div
                  className="text-sm font-semibold text-[#0F172A] truncate"
                  data-testid={TID.clientHeaderName}
                >
                  {client.name}
                </div>
                <div className="text-[11px] text-[#64748B] mt-0.5">
                  {companyTypeLabel(client.type)}
                </div>
              </div>
              {onEditClient && (
                <button
                  type="button"
                  onClick={onEditClient}
                  data-testid={TID.clientHeaderEdit}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC] ml-1"
                  title="Edit client"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {variant === "overview" && (
            <div className="hidden lg:flex items-center gap-3 pl-4 ml-2 border-l border-[#E2E8F0]">
              <span
                className="text-xs text-[#64748B]"
                data-testid={TID.headerDate}
              >
                {formatToday()} · {formatClock(now)}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 text-xs text-[#64748B] mr-2">
            <span
              className="inline-flex items-center gap-1.5"
              data-testid={TID.headerDbStatus}
              title={dbConnected ? "Database connected" : "Database offline"}
            >
              <span
                className="ab-dot"
                style={{ background: dbConnected ? "#16A34A" : "#DC2626" }}
              />
              <span>{dbConnected ? "Live" : "Offline"}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onOpenLibrary}
            data-testid={TID.libraryBtn}
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm font-medium text-[#334155] hover:border-[#CBD5E1]"
            title="Master compliance library"
            style={!onOpenLibrary ? { display: "none" } : undefined}
          >
            <Library className="h-4 w-4" />
            Library
          </button>
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid={TID.settingsBtn}
            className="hidden sm:inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[#E2E8F0] bg-white text-[#334155] hover:border-[#CBD5E1]"
            title="Reminder settings"
            style={!onOpenSettings ? { display: "none" } : undefined}
          >
            <SettingsIcon className="h-4 w-4" />
          </button>
          {primaryLabel && (
            <Button
              onClick={onPrimary}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white h-9 px-3.5 rounded-lg"
              data-testid={primaryTestId || TID.addTaskBtn}
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">{primaryLabel}</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
